import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Brain, Wifi, BarChart3, Database, TrendingUp, CheckCircle2, Zap, Target, Shield, Settings, ArrowRight } from 'lucide-react';

const DigitalSolutionsPage = () => {
  const solutions = [
    {
      icon: <Database className="w-8 h-8" />,
      title: 'Sustainability Data Platforms',
      description: 'Centralized platforms for collecting, managing, and analyzing sustainability data across your organization.',
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: 'AI-Driven Analytics Tools',
      description: 'Intelligent systems that transform raw data into actionable insights using machine learning and AI.',
    },
    {
      icon: <Wifi className="w-8 h-8" />,
      title: 'IoT Monitoring Systems',
      description: 'Real-time sensor networks for environmental monitoring, energy tracking, and operational efficiency.',
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: 'ESG / SDGs Dashboards',
      description: 'Comprehensive dashboards for tracking ESG metrics, SDG alignment, and sustainability KPIs.',
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Carbon Dashboards',
      description: 'Advanced carbon footprint tracking and emissions monitoring tools for climate action.',
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Decision-Support Systems',
      description: 'Strategic tools that provide recommendations and scenario analysis for sustainability planning.',
    },
  ];

  const keyFeatures = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Real-Time Monitoring',
      description: 'Continuous data collection and instant visibility into sustainability performance',
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'KPI Tracking',
      description: 'Automated tracking and reporting of key sustainability indicators',
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Regulatory Compliance Support',
      description: 'Built-in compliance frameworks aligned with EU and international standards',
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: 'Integration with Existing Systems',
      description: 'Seamless connection with your current IT infrastructure and workflows',
    },
  ];

  const valueProps = [
    {
      title: 'Better Decisions',
      description: 'Data-driven insights enable informed strategic choices for sustainability initiatives',
      icon: <Target className="w-12 h-12" />,
    },
    {
      title: 'Improved Performance',
      description: 'Optimize operations, reduce waste, and enhance efficiency across all sustainability metrics',
      icon: <TrendingUp className="w-12 h-12" />,
    },
    {
      title: 'Measurable Impact',
      description: 'Track, quantify, and demonstrate the tangible results of your sustainability efforts',
      icon: <CheckCircle2 className="w-12 h-12" />,
    },
  ];

  const capabilities = [
    'AI-enabled sustainability tools',
    'IoT-based monitoring systems',
    'Data platforms and dashboards',
    'Decision-support systems',
    'Performance tracking and reporting',
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1644088379091-d574269d422f"
            alt="Digital Network"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Digital Solutions</h1>
            <p className="text-2xl text-gray-300 leading-relaxed mb-8">
              AI-enabled sustainability tools · IoT-based monitoring systems · Data platforms and dashboards · Decision-support systems · Performance tracking and reporting
            </p>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">Overview</h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                SUSTAINESIS GLOBAL LTD develops digital systems that enable organisations to monitor, manage, and optimise sustainability performance.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Our solutions integrate AI, IoT, and advanced analytics to provide real-time insights, automated reporting, and data-driven decision support. From carbon tracking to ESG compliance, our platforms transform complex sustainability data into actionable intelligence.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We design scalable, user-friendly systems that connect seamlessly with existing infrastructure, empowering organizations to achieve measurable impact and meet their sustainability goals with confidence.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1759661966728-4a02e3c6ed91?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Digital Analytics Dashboard"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-red-600 text-white p-6 rounded-xl shadow-xl">
                <Database className="w-10 h-10 mb-2" />
                <p className="text-sm font-bold">Data-Driven Sustainability</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Our Solutions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive digital tools for sustainability management
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center text-white mb-6">
                    {solution.icon}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-4">{solution.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{solution.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Key Features</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Powerful capabilities that drive sustainability excellence
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {keyFeatures.map((feature, index) => (
              <div key={index} className="flex items-start space-x-6 p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-red-600">
                <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-black mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Capabilities</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              End-to-end digital solutions for sustainability transformation
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/20 hover:border-red-600 hover:bg-red-600/20 rounded-full text-lg font-semibold transition-all duration-300"
              >
                {capability}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Value</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Turning data into actionable insights for:
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {valueProps.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-24 h-24 bg-red-600 rounded-2xl flex items-center justify-center text-white mb-6 mx-auto transform hover:scale-110 transition-transform duration-300">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed text-lg">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">Why Digital Solutions Matter</h2>
              <div className="space-y-6">
                {[
                  'Real-time visibility into sustainability performance',
                  'Data-driven decision making for climate action',
                  'Automated compliance and reporting',
                  'Scalable monitoring across multiple sites',
                  'Predictive insights for proactive management',
                  'Seamless integration with existing workflows',
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 rounded-lg bg-white shadow-md">
                    <CheckCircle2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-gray-800 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71"
                alt="Data Analytics Dashboard"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-red-600 text-white p-8 rounded-xl shadow-2xl">
                <Database className="w-12 h-12 mb-4" />
                <p className="text-lg font-bold">Transform Your Data</p>
                <p className="text-sm text-white/90 mt-2">Into Actionable Insights</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SustainHub365 Showcase */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Our Digital Solution in Action</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real-world platform delivering measurable sustainability impact
            </p>
          </div>

          <Card className="border-0 shadow-2xl overflow-hidden">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-5 gap-0">
                {/* Image Section */}
                <div className="md:col-span-2 bg-gradient-to-br from-gray-900 to-gray-800 p-12 flex items-center justify-center">
                  <div className="text-center">
                    <div className="mb-8">
                      <Database className="w-24 h-24 text-red-600 mx-auto mb-6" />
                      <h3 className="text-3xl font-bold text-white mb-2">SUSTAINHUB365</h3>
                      <p className="text-gray-400">Next-Generation Platform</p>
                    </div>
                    <a 
                      href="https://sustainhub365.com/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-block"
                    >
                      <Button className="bg-red-600 hover:bg-red-700 text-white">
                        Visit Platform
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Content Section */}
                <div className="md:col-span-3 p-12 bg-white">
                  <h3 className="text-3xl font-bold text-black mb-6">
                    Measure, Manage, and Improve Sustainability Performance
                  </h3>
                  <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                    SUSTAINHUB365 is a next-generation digital platform designed to help organizations measure, manage, and improve sustainability performance across operations, supply chains, and destinations. It brings together environmental data, regulatory compliance, and performance monitoring into a single, intelligent ecosystem.
                  </p>

                  <div className="grid grid-cols-2 gap-6 mb-8">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <Zap className="w-5 h-5 text-red-600" />
                      </div>
                      <span className="text-gray-800 font-medium">Energy</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <Database className="w-5 h-5 text-red-600" />
                      </div>
                      <span className="text-gray-800 font-medium">Water</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-red-600" />
                      </div>
                      <span className="text-gray-800 font-medium">Waste</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-red-600" />
                      </div>
                      <span className="text-gray-800 font-medium">Emissions</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-red-600">
                    <h4 className="font-bold text-black mb-3">Key Features:</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Cloud-based ESG performance tracking</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Aligned with GSTC, GRI, CSRD, EU Taxonomy, SBTi, ISO standards</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">AI-supported analytics and smart dashboards</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Modular, scalable, and sector-agnostic</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Transform Your Data Into Action</h2>
          <p className="text-xl mb-10 text-white/90">
            Discover how our digital solutions can accelerate your sustainability journey
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-black hover:bg-gray-900 text-white text-lg px-10 py-6">
              Schedule a Demo
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default DigitalSolutionsPage;
